import { createContext, type ReactNode } from 'react';

/**
 * The error-toast telemetry seam. When a `destructive` / `warning` toast is
 * shown, the `Toast` component invokes `onErrorToast` (if a host app supplied
 * one). This is the injection point that keeps the design-system package
 * app-agnostic: the package NEVER imports app analytics
 * (`@/utils/analytics/*`, `@/models/analytics/*`) — the host wires the callback
 * to whatever telemetry it uses (in this repo, `trackErrorViewed`), doing the
 * `tone → severity` and surface mapping app-side.
 *
 * Mirrors the `ExperimentalDesignSystemContext` DI pattern: host apps mount the
 * provider with app-derived values; components read via `useContext`.
 */
export interface ToastAnalyticsContextValue {
  /**
   * Called once when an error-class toast (`tone: 'destructive' | 'warning'`)
   * is shown. `message` is the toast's user-facing text (description, falling
   * back to title). Absent by default — the design system fires no telemetry
   * unless a host opts in.
   */
  onErrorToast?: (info: { message: string; tone: 'destructive' | 'warning' }) => void;
}

export const ToastAnalyticsContext = createContext<ToastAnalyticsContextValue>({});

export function ToastAnalyticsContextProvider({
  children,
  value,
}: {
  children: ReactNode;
  value: ToastAnalyticsContextValue;
}) {
  return (
    <ToastAnalyticsContext.Provider value={value}>
      {children}
    </ToastAnalyticsContext.Provider>
  );
}
